'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import useAuthStore from '@/store/useAuthStore';
import { useDeviceType } from '@/lib/hooks';
import useAudioStore from '@/store/useAudioStore';
import useConfigStore from '@/store/useConfigStore';
import useSubscriptionStore from '@/store/useSubscriptionStore';
import useThemeStore from '@/store/useThemeStore';
import report, { ReportEvent } from '@/lib/report';
import { useUTMTracking } from '@/hooks/useUTMTracking';
import { useAppModules } from '@/hooks/useAppModules';
import { UserBookshelfService } from '@/lib/userBookshelfService';
import {
  getOnboardingEntryPath,
  getPostLoginPath,
} from '@/lib/onboarding-routing';
import { getCanonicalProfileRole, isParentProfile } from '@/lib/profile-role';

export default function RootAuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const { user, profile, checkAuth } = useAuthStore();
  const device = useDeviceType();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const interruptAudio = useAudioStore(state => state.interruptAudio);
  const [isMounted, setIsMounted] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const { colors } = useThemeStore();
  
  // Initialize UTM tracking
  const { updateIdentityOnLogin } = useUTMTracking();

  // Initialize all app modules (console disable, chunk error, auto refresh, system sound, etc.)
  useAppModules();

  // Define public routes that don't require authentication
  const publicRoutes = ['/', '/forgot-password', '/reset-password', '/verify-email', '/google_classroom', '/flowquiz', '/search', '/extension-login', '/extension', '/privacy-policy', '/terms', '/roadmaps', '/textbooks', '/blog', '/subjects', '/about', '/for-parents', '/live-tutor-for-parents', '/scratch-ai-tutor', '/phet-ai-tutor', '/ixl-khan-ai-tutor', '/parent/checkout'];

  // Define public route prefixes (for routes like /magic-lab/[id])
  const publicRoutePrefixes = ['/magic-lab/', '/teacher-landing', '/extension/', '/search', '/learn/', '/practice/', '/blog/', '/subjects/', '/grade/', '/skills/', '/textbooks/', '/tools/', '/compare/'];

  // Check if current route is a homework share link on flowquiz.ai domain
  // Format: flowquiz.ai/[shortCode] (e.g., flowquiz.ai/abc123)
  const isFlowQuizDomain = typeof window !== 'undefined' &&
    (window.location.hostname === 'flowquiz.ai' || window.location.hostname === 'www.flowquiz.ai');
  const isHomeworkShareLink = isFlowQuizDomain && pathname.match(/^\/[a-zA-Z0-9]+$/) && pathname !== '/';

  // Check if current route is a homework player page (has ?hw=xxx parameter)
  const isHomeworkPlayerPage = pathname.startsWith('/quest/flowquiz/player/') &&
    searchParams.get('hw') !== null;

  // Check if current route requires authentication
  const requiresAuth = !publicRoutes.includes(pathname) &&
                       !publicRoutePrefixes.some(prefix => pathname.startsWith(prefix)) &&
                       !isHomeworkShareLink &&
                       !isHomeworkPlayerPage;
  const shouldBypassAuthBlocking = !requiresAuth;

  // When a logged-in user is on landing page, we immediately show loading until redirect finishes.
  const shouldHoldLandingForAuthRedirect = pathname === '/' && !!user && !isFlowQuizDomain;

  // 浏览器地址发生改变，打断音频
  useEffect(() => {
    interruptAudio();

    report(ReportEvent.PATH_NAME_CHANGE, {
      path: pathname,
      location: window.location.href,
    });
  }, [pathname]);

  useEffect(() => {
    useConfigStore.setState({ deviceType: device });
  }, [device]);

  // Handle client-side mounting to avoid hydration mismatch
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Handle authentication initialization
  useEffect(() => {
    if (!isMounted) return;

    const initAuth = async () => {
      console.log('Initializing authentication...');

      try {
        // Check if user is already authenticated through session
        const currentUser = await checkAuth();

        if (currentUser) {
          console.log('Authenticated user found:', currentUser.email);

          // Associate UTM attribution with the authenticated user
          try {
            await updateIdentityOnLogin(currentUser.id, currentUser.email);
            console.log('UTM attribution updated for user:', currentUser.email);
          } catch (error) {
            console.error('Failed to update UTM attribution:', error);
          }
        } else {
          console.log('No authenticated user found, user needs to sign in');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      } finally {
        setAuthChecked(true);
      }
    };

    initAuth();
  }, [isMounted, checkAuth, pathname]);

  // Load subscription data globally once user is authenticated
  useEffect(() => {
    if (!authChecked || !user?.id) return;
    const store = useSubscriptionStore.getState();
    if (!store.initialized) {
      store.loadForStudent(user.id);
    }
    // Listen for realtime subscription changes (e.g. mid-session upgrade)
    const unsub = store.subscribeRealtime(user.id);
    return unsub;
  }, [authChecked, user?.id]);

  // Handle authentication and business logic routing
  useEffect(() => {
    if (!isMounted || !authChecked) return;

    let cancelled = false;

    const handleRouting = async () => {
      // Handle home page routing logic
      if (pathname === '/') {
        // Skip redirect for flowquiz.ai - it has its own landing page via middleware rewrite
        const isFlowQuizDomain = typeof window !== 'undefined' &&
          (window.location.hostname === 'flowquiz.ai' || window.location.hostname === 'www.flowquiz.ai');

        if (isFlowQuizDomain) {
          console.log('FlowQuiz domain detected, skipping redirect');
          return;
        }

        if (user && profile) {
          console.log('User authenticated on landing page, checking profile and redirect...');
          console.log('🐘 profiles is', profile);

          // Check if there's a redirect parameter in the URL
          const searchParams = new URLSearchParams(window.location.search);
          const redirectPath = searchParams.get('redirect');
          console.log('Redirect path from URL:', redirectPath);

          if (profile.is_onboarding_finished) {
            const canonicalRole = getCanonicalProfileRole(profile);

            // Parent v2 should always land on /parent even when redirect query exists.
            if (isParentProfile(profile)) {
              if (!cancelled) {
                router.push('/parent');
              }
              return;
            }

            // If onboarding is finished and there's a redirect parameter, go there
            if (redirectPath) {
              if (!cancelled) {
                console.log('Redirecting authenticated user to:', redirectPath);
                router.push(redirectPath);
              }
              return;
            }

            const profileAny = profile as any;
            const isParentCreatedManagedStudent =
              canonicalRole === 'student' &&
              profileAny?.account_source === 'parent_created';

            // Parent-created students should finish bookshelf selection in /onboard/prepare first.
            if (isParentCreatedManagedStudent && user.id) {
              const bookshelf = await UserBookshelfService.getBookshelf(user.id);
              const hasBookshelfSelection =
                Array.isArray(bookshelf?.quest_ids) && bookshelf.quest_ids.length > 0;

              if (!hasBookshelfSelection) {
                if (!cancelled) {
                  console.log('Managed student missing bookshelf selection, redirecting to /onboard/prepare');
                  router.push('/onboard/prepare');
                }
                return;
              }
            }

            if (!cancelled) {
              // Default post-login destination from landing page
              router.push(getPostLoginPath(profile, redirectPath));
            }
          } else {
            if (!cancelled) {
              const onboardingPath = getOnboardingEntryPath(profile, redirectPath);
              console.log('User needs onboarding, redirecting to:', onboardingPath);
              router.push(onboardingPath);
            }
          }
        }
        return;
      }

      // Handle other protected routes
      if (requiresAuth && !user) {
        console.log('Redirecting to signin page - no authenticated user found');
        // Include the current path and search params as redirect parameter
        const currentUrl = `${pathname}${window.location.search}`;
        const redirectUrl = `/?redirect=${encodeURIComponent(currentUrl)}`;
        if (!cancelled) {
          router.push(redirectUrl);
        }
      }
    };

    void handleRouting();

    return () => {
      cancelled = true;
    };
  }, [user, profile, authChecked, requiresAuth, router, isMounted, pathname]);

  // Prevent hydration mismatch by not rendering dynamic content until mounted
  if (shouldHoldLandingForAuthRedirect || (!shouldBypassAuthBlocking && (!isMounted || !authChecked))) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <div className="emphasize-text text-3xl font-bold" style={{ color: colors.loadingColor }}>Loading...</div>
      </div>
    );
  }

  // if (device === 'mobile') {
  //   return (
  //     <div className="w-full h-screen flex justify-center items-center px-10">
  //       <div className="emphasize-text text-3xl font-bold" style={{ color: colors.secondaryColor }}>Please open this website on a PC or tablet</div>
  //     </div>
  //   )
  // }

  return <>{children}</>;
} 
